/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v3";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import * as types from "../../types/primitives.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  ConditionGroup,
  ConditionGroup$inboundSchema,
  ConditionGroup$Outbound,
  ConditionGroup$outboundSchema,
} from "./conditiongroup.js";

/**
 * The logical operator used to combine the condition groups.
 */
export const NestedConditionExpressionOperator = {
  LogicalAnd: "logical_and",
  LogicalOr: "logical_or",
} as const;
/**
 * The logical operator used to combine the condition groups.
 */
export type NestedConditionExpressionOperator = OpenEnum<
  typeof NestedConditionExpressionOperator
>;

/**
 * Allows for nested condition logic like (A AND B) OR (C AND D).
 */
export type NestedConditionExpression = {
  /**
   * An array of condition groups to be combined with the logical operator.
   */
  groups: Array<ConditionGroup>;
  /**
   * Optional name for the condition, useful for debugging and documentation.
   */
  name?: string | undefined;
  /**
   * Whether to negate the entire nested expression result.
   */
  negate?: boolean | undefined;
  /**
   * The logical operator used to combine the condition groups.
   */
  operator?: NestedConditionExpressionOperator | undefined;
};

/** @internal */
export const NestedConditionExpressionOperator$inboundSchema: z.ZodType<
  NestedConditionExpressionOperator,
  z.ZodTypeDef,
  unknown
> = openEnums.inboundSchema(NestedConditionExpressionOperator);
/** @internal */
export const NestedConditionExpressionOperator$outboundSchema: z.ZodType<
  string,
  z.ZodTypeDef,
  NestedConditionExpressionOperator
> = openEnums.outboundSchema(NestedConditionExpressionOperator);

/** @internal */
export const NestedConditionExpression$inboundSchema: z.ZodType<
  NestedConditionExpression,
  z.ZodTypeDef,
  unknown
> = z.object({
  groups: z.array(ConditionGroup$inboundSchema),
  name: types.optional(types.string()),
  negate: types.boolean().default(false),
  operator: NestedConditionExpressionOperator$inboundSchema.default(
    "logical_and",
  ),
});
/** @internal */
export type NestedConditionExpression$Outbound = {
  groups: Array<ConditionGroup$Outbound>;
  name?: string | undefined;
  negate: boolean;
  operator: string;
};

/** @internal */
export const NestedConditionExpression$outboundSchema: z.ZodType<
  NestedConditionExpression$Outbound,
  z.ZodTypeDef,
  NestedConditionExpression
> = z.object({
  groups: z.array(ConditionGroup$outboundSchema),
  name: z.string().optional(),
  negate: z.boolean().default(false),
  operator: NestedConditionExpressionOperator$outboundSchema.default(
    "logical_and",
  ),
});

export function nestedConditionExpressionToJSON(
  nestedConditionExpression: NestedConditionExpression,
): string {
  return JSON.stringify(
    NestedConditionExpression$outboundSchema.parse(nestedConditionExpression),
  );
}
export function nestedConditionExpressionFromJSON(
  jsonString: string,
): SafeParseResult<NestedConditionExpression, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => NestedConditionExpression$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'NestedConditionExpression' from JSON`,
  );
}
